import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { DEV_BYPASS_TEST_MODE, logDevBypassBanner } from '@/lib/devBypass';


export type Plan = 'free' | 'monthly_premium' | 'annual_founding' | 'annual_standard';
export type Role = 'owner' | 'admin' | 'member';

export interface Profile {
  id: string;
  email: string | null;
  name: string | null;
  family_name: string | null;
  plan: Plan;
  role: Role;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}

interface AuthContextType {
  // session
  user: User | null;
  session: Session | null;
  loading: boolean; // initial bootstrap (auth + profile)
  profileLoading: boolean; // true while profile is being fetched (after auth resolved)
  profileError: string | null; // set when profile fetch failed; UI should show retry, NOT free fallback

  // profile / plan / role
  profile: Profile | null;
  plan: Plan;
  role: Role;
  isPaid: boolean;
  isFree: boolean;
  isAuthenticated: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isStaff: boolean; // owner OR admin

  // convenience aliases for legacy code
  memberEmail: string | null;
  memberName: string | null;
  familyName: string | null;

  // actions
  signUp: (
    email: string,
    password: string,
    meta?: { name?: string; familyName?: string }
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  signOut: () => Promise<void>;
  sendPasswordReset: (
    email: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  resendConfirmationEmail: (
    email: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  sendMagicLink: (
    email: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;

  updatePassword: (
    newPassword: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  refreshProfile: () => Promise<void>;
  updateProfile: (
    patch: Partial<
      Pick<
        Profile,
        'name' | 'family_name' | 'plan' | 'role' | 'stripe_customer_id' | 'stripe_subscription_id'
      >
    >
  ) => Promise<void>;
  setFamilyName: (name: string) => Promise<void>;
  promoteToOwnerIfNeeded: () => Promise<void>;
  /**
   * Phase 9 — Paid Membership Recognition Sync.
   *
   * Reconciles the local profiles row against the Stripe gateway by EMAIL
   * (case-insensitive, trimmed). If the gateway shows an active /
   * trialing / past_due subscription on a recognized KinsCircle price
   * (yearly Founder's $14.99, yearly Standard $19.99, monthly $4.99) and
   * the local plan is below that level, the matching profile row's
   * `plan`, `stripe_customer_id`, and `stripe_subscription_id` are
   * upgraded to match.
   *
   * NEVER downgrades. NEVER creates a checkout. NEVER mutates pricing,
   * Stripe connection, family graph, media, invites, or auth credentials.
   * Always safe to call. Returns the structured result the edge function
   * returned, or null on failure (callers should treat null as "leave
   * the existing local plan alone").
   */
  syncMembershipFromPayments: () => Promise<{ updated: boolean; plan: Plan | null; reason: string } | null>;
}



const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PROFILE_COLUMNS =
  'id,email,name,family_name,plan,role,stripe_customer_id,stripe_subscription_id';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const profileFetchInFlight = useRef<string | null>(null);

  const fetchProfile = useCallback(async (userId: string, userEmail?: string | null) => {
    if (profileFetchInFlight.current === userId) return;
    profileFetchInFlight.current = userId;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[Auth] profile fetch failed', error);
        // CRITICAL: do NOT fabricate a free profile on failure. Surface the
        // error so the UI shows a loading/retry state instead of treating
        // the user as a free member.
        setProfileError(error.message || 'Failed to load profile');
        return;
      }

      if (data) {
        setProfileError(null);
        setProfile({
          id: data.id,
          email: data.email ?? userEmail ?? null,
          name: data.name ?? null,
          family_name: data.family_name ?? null,
          plan: (data.plan as Plan) ?? 'free',
          role: (data.role as Role) ?? 'member',
          stripe_customer_id: data.stripe_customer_id ?? null,
          stripe_subscription_id: data.stripe_subscription_id ?? null,
        });
        return;
      }

      // No profile row — try to create one. Only here is 'free/member' a
      // legitimate default (brand new account without any data yet).
      const { data: created, error: insErr } = await supabase
        .from('profiles')
        .insert({ id: userId, email: userEmail ?? null, plan: 'free', role: 'member' })
        .select(PROFILE_COLUMNS)
        .maybeSingle();
      if (insErr) {
        console.error('[Auth] profile create failed', insErr);
        setProfileError(insErr.message || 'Failed to create profile');
        return;
      }
      if (created) {
        setProfileError(null);
        setProfile({
          id: created.id,
          email: created.email ?? null,
          name: created.name ?? null,
          family_name: created.family_name ?? null,
          plan: (created.plan as Plan) ?? 'free',
          role: (created.role as Role) ?? 'member',
          stripe_customer_id: created.stripe_customer_id ?? null,
          stripe_subscription_id: created.stripe_subscription_id ?? null,
        });
      }
    } catch (err: any) {
      console.error('[Auth] profile fetch exception', err);
      setProfileError(err?.message || 'Failed to load profile');
    } finally {
      profileFetchInFlight.current = null;
      setProfileLoading(false);
    }
  }, []);

  // Bootstrap: read session, then subscribe to changes.
  //
  // CRITICAL: this runs for EVERY user role on every page load. It MUST
  // always flip `loading` to false within a bounded window, even if
  // supabase.auth.getSession() or the profile fetch hang. Otherwise
  // downstream consumers (MembershipContext, AppLayout) sit in a loading
  // state forever and the page appears blank/non-loading. We cap the
  // initial bootstrap at 8 seconds.
  useEffect(() => {
    let mounted = true;
    const BOOT_TIMEOUT_MS = 8000;

    // Hard timeout — never let `loading` stay true past this. Even if
    // both getSession() AND fetchProfile() hang, we still flip the flag
    // so the app can render (and AppLayout can show the recovery screen
    // if needed). The user can still be authenticated; we just don't
    // block initial render on async confirmation.
    const hardTimeout = window.setTimeout(() => {
      if (!mounted) return;
      console.warn(
        '[Auth] bootstrap timed out after',
        BOOT_TIMEOUT_MS,
        'ms — flipping loading=false so the app can render. Session/profile will continue to resolve in the background via onAuthStateChange.',
      );
      setLoading(false);
    }, BOOT_TIMEOUT_MS);

    const bootstrap = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        const s = data.session ?? null;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          // Fire-and-forget. Profile loading lives on its own
          // profileLoading flag and must NOT keep the global `loading`
          // flag spinning if the profile query stalls.
          fetchProfile(s.user.id, s.user.email).catch((e) =>
            console.warn('[Auth] fetchProfile during bootstrap failed', e),
          );
        }
      } catch (err) {
        console.error('[Auth] bootstrap failed', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    bootstrap();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
      setUser(s?.user ?? null);
      if (s?.user) {
        // fire-and-forget; don't flip global loading on session refreshes
        fetchProfile(s.user.id, s.user.email).catch((e) =>
          console.warn('[Auth] fetchProfile on auth-change failed', e),
        );
      } else {
        setProfile(null);
        setProfileError(null);
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(hardTimeout);
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);


  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id, user.email);
  }, [user, fetchProfile]);

  const signUp = useCallback<AuthContextType['signUp']>(
    async (email, password, meta) => {
      try {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              name: meta?.name?.trim() || '',
              family_name: meta?.familyName?.trim() || '',
            },
          },
        });
        if (error) return { ok: false, error: error.message };
        if (data.user) {
          await fetchProfile(data.user.id, data.user.email);
        }
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err?.message || 'Sign up failed' };
      }
    },
    [fetchProfile]
  );

  const signIn = useCallback<AuthContextType['signIn']>(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) return { ok: false, error: error.message };
      if (data.user) {
        await fetchProfile(data.user.id, data.user.email);
      }
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Sign in failed' };
    }
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    console.log('[Auth] verified sign-out starting...');

    let globalSignOutError: Error | null = null;

    try {
      const { error } =
        await supabase.auth.signOut({
          scope: 'global',
        });

      if (error) {
        globalSignOutError = error;
        console.warn(
          '[Auth] global sign-out failed; forcing local session termination',
          error.message,
        );
      }
    } catch (unknownError: unknown) {
      globalSignOutError =
        unknownError instanceof Error
          ? unknownError
          : new Error(String(unknownError));

      console.warn(
        '[Auth] global sign-out threw; forcing local session termination',
        globalSignOutError.message,
      );
    }

    if (globalSignOutError) {
      const { error: localError } =
        await supabase.auth.signOut({
          scope: 'local',
        });

      if (localError) {
        throw new Error(
          `Could not terminate the local session: ${localError.message}`,
        );
      }
    }

    let {
      data: {
        session: remainingSession,
      },
    } = await supabase.auth.getSession();

    if (remainingSession) {
      const { error: forcedLocalError } =
        await supabase.auth.signOut({
          scope: 'local',
        });

      if (forcedLocalError) {
        throw new Error(
          `Session remained active after sign-out: ${forcedLocalError.message}`,
        );
      }

      const verification =
        await supabase.auth.getSession();

      remainingSession =
        verification.data.session;
    }

    if (remainingSession) {
      throw new Error(
        'Session termination could not be verified.',
      );
    }

    setProfile(null);
    setUser(null);
    setSession(null);
    setProfileError(null);
    setProfileLoading(false);
    profileFetchInFlight.current = null;
    autoSyncFiredRef.current = null;

    console.log(
      '[Auth] session termination verified',
    );
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Production password-reset email pipeline.
  //
  // PROBLEM (root cause of "shows sent but never arrives"):
  //   supabase.auth.resetPasswordForEmail() returns { error: null } even
  //   when Supabase's underlying SMTP relay silently drops the message —
  //   default Supabase SMTP is heavily rate-limited and rejects FROM
  //   addresses that aren't on a verified domain. So the UI falsely
  //   reported "sent" while users never received anything.
  //
  // FIX:
  //   We now route password-reset / magic-link delivery through our own
  //   verified Resend HTTP pipeline (the SAME provider already handling
  //   invites + family activity emails reliably) by calling the
  //   `send-test-email` edge function in `mode: 'auth'`. That path uses
  //   service-role admin.generateLink() to mint a real, signed Supabase
  //   action link, then sends it via Resend. Returns structured ok/error
  //   so the UI shows real success/failure (no more fake toasts).
  //
  //   If the edge-function path is unavailable (e.g. older deploy that
  //   doesn't yet understand mode:'auth', network blip, etc.) we FALL
  //   BACK to supabase.auth.resetPasswordForEmail() so users still have
  //   a path forward — and we log loudly that the fallback fired so an
  //   operator can investigate in the function logs.
  //
  //   redirectTo MUST point at the production domain — Supabase's URL
  //   allow-list silently refuses to send unless the URL is whitelisted.
  // ─────────────────────────────────────────────────────────────────────────
  const sendPasswordReset = useCallback<AuthContextType['sendPasswordReset']>(
    async (email) => {
      const trimmed = email.trim();
      if (!trimmed) return { ok: false, error: 'Please enter your email address.' };
      const isLocalDev =
        typeof window !== 'undefined' &&
        /^(localhost|127\.0\.0\.1|.*\.local)$/i.test(window.location.hostname);
      const redirectTo = isLocalDev
        ? `${window.location.origin}/reset-password`
        : 'https://www.kinscircleapp.com/reset-password';

      console.log('[Auth] password-reset request', { email: trimmed, redirectTo, pipeline: 'resend-edge' });

      // Primary: Resend HTTP via send-test-email mode:'auth'
      try {
        const { data, error } = await supabase.functions.invoke('send-test-email', {
          body: { mode: 'auth', email: trimmed, kind: 'recovery', redirectTo },
        });
        if (!error && data && (data as any).ok === true) {
          console.log('[Auth] password-reset delivered via Resend', {
            messageId: (data as any).messageId ?? null,
            delivered: (data as any).delivered ?? null,
          });
          return { ok: true };
        }
        // Edge function ran but reported a real delivery failure
        if (!error && data && (data as any).ok === false && (data as any).error) {
          console.error('[Auth] password-reset edge function returned error:', (data as any));
          // If the function predates the auth-mode path it may not recognize
          // mode:'auth' and return a generic error. Fall through to the
          // Supabase fallback in that case.
          const errMsg = String((data as any).error || '');
          const looksUnsupported =
            /missing authorization/i.test(errMsg) ||
            /not authenticated/i.test(errMsg) ||
            /unsupported|unknown mode/i.test(errMsg);
          if (!looksUnsupported) {
            return { ok: false, error: errMsg };
          }
          console.warn('[Auth] auth-mode unsupported in deployed edge function — falling back to supabase.auth');
        } else if (error) {
          console.warn('[Auth] send-test-email invoke error, falling back to supabase.auth:', error.message);
        }
      } catch (err: any) {
        console.warn('[Auth] send-test-email invoke threw, falling back to supabase.auth:', err?.message || err);
      }

      // Fallback: legacy supabase.auth.resetPasswordForEmail
      try {
        console.log('[Auth] password-reset fallback → supabase.auth.resetPasswordForEmail (Supabase SMTP)');
        const response = await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo });
        if (response.error) {
          console.error('[Auth] resetPasswordForEmail failed:', response.error);
          return { ok: false, error: response.error.message };
        }
        console.warn('[Auth] password-reset accepted by Supabase SMTP — delivery NOT verified. If users report missing emails, configure custom SMTP or Resend at the Supabase Auth level.');
        return { ok: true };
      } catch (err: any) {
        console.error('[Auth] password-reset fallback threw:', err);
        return { ok: false, error: err?.message || 'Failed to send reset email' };
      }
    },
    []
  );


  // Resend the email-confirmation link for an account that signed up but
  // never clicked the link. Uses Supabase's auth.resend with type='signup'.
  const resendConfirmationEmail = useCallback<AuthContextType['resendConfirmationEmail']>(
    async (email) => {
      try {
        const trimmed = email.trim();
        if (!trimmed) return { ok: false, error: 'Please enter your email address.' };
        const isLocalDev =
          typeof window !== 'undefined' &&
          /^(localhost|127\.0\.0\.1|.*\.local)$/i.test(window.location.hostname);
        const emailRedirectTo = isLocalDev
          ? `${window.location.origin}/auth/callback`
          : 'https://www.kinscircleapp.com/auth/callback';

        console.log('[Auth] resending confirmation email', { email: trimmed, emailRedirectTo });
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: trimmed,
          options: { emailRedirectTo },
        });
        if (error) {
          console.error('[Auth] resend confirmation failed', error);
          return { ok: false, error: error.message };
        }
        return { ok: true };
      } catch (err: any) {
        console.error('[Auth] resend confirmation exception', err);
        return { ok: false, error: err?.message || 'Failed to resend confirmation email' };
      }
    },
    []
  );


  // Magic-link sign-in. Same primary→fallback strategy as sendPasswordReset
  // so a single broken Supabase SMTP doesn't break both flows.
  const sendMagicLink = useCallback<AuthContextType['sendMagicLink']>(
    async (email) => {
      const trimmed = email.trim();
      if (!trimmed) return { ok: false, error: 'Please enter your email address.' };
      const isLocalDev =
        typeof window !== 'undefined' &&
        /^(localhost|127\.0\.0\.1|.*\.local)$/i.test(window.location.hostname);
      const redirectTo = isLocalDev
        ? `${window.location.origin}/auth/callback`
        : 'https://www.kinscircleapp.com/auth/callback';

      console.log('[Auth] magic-link request', { email: trimmed, redirectTo, pipeline: 'resend-edge' });

      // Primary: Resend HTTP
      try {
        const { data, error } = await supabase.functions.invoke('send-test-email', {
          body: { mode: 'auth', email: trimmed, kind: 'magiclink', redirectTo },
        });
        if (!error && data && (data as any).ok === true) {
          console.log('[Auth] magic-link delivered via Resend', { messageId: (data as any).messageId ?? null });
          return { ok: true };
        }
        if (!error && data && (data as any).ok === false && (data as any).error) {
          const errMsg = String((data as any).error || '');
          const looksUnsupported =
            /missing authorization/i.test(errMsg) ||
            /not authenticated/i.test(errMsg) ||
            /unsupported|unknown mode/i.test(errMsg);
          if (!looksUnsupported) {
            console.error('[Auth] magic-link edge function returned error:', errMsg);
            return { ok: false, error: errMsg };
          }
          console.warn('[Auth] auth-mode unsupported — falling back to supabase.auth.signInWithOtp');
        } else if (error) {
          console.warn('[Auth] magic-link invoke error, falling back to supabase.auth:', error.message);
        }
      } catch (err: any) {
        console.warn('[Auth] magic-link invoke threw, falling back to supabase.auth:', err?.message || err);
      }

      // Fallback: legacy signInWithOtp
      try {
        console.log('[Auth] magic-link fallback → supabase.auth.signInWithOtp (Supabase SMTP)');
        const response = await supabase.auth.signInWithOtp({
          email: trimmed,
          options: { emailRedirectTo: redirectTo, shouldCreateUser: true },
        });
        if (response.error) {
          console.error('[Auth] signInWithOtp failed:', response.error);
          return { ok: false, error: response.error.message };
        }
        console.warn('[Auth] magic-link accepted by Supabase SMTP — delivery NOT verified.');
        return { ok: true };
      } catch (err: any) {
        console.error('[Auth] magic-link fallback threw:', err);
        return { ok: false, error: err?.message || 'Failed to send magic link' };
      }
    },
    []
  );




  // Update the currently-signed-in user's password (used on the
  // /reset-password page after they click the email link).
  const updatePassword = useCallback<AuthContextType['updatePassword']>(
    async (newPassword) => {
      try {
        if (!newPassword || newPassword.length < 6) {
          return { ok: false, error: 'Password must be at least 6 characters.' };
        }
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) return { ok: false, error: error.message };
        console.log('User logged in successfully');
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err?.message || 'Failed to update password' };
      }
    },
    []
  );



  const updateProfile = useCallback<AuthContextType['updateProfile']>(
    async (patch) => {
      if (!user) return;
      const { error } = await supabase
        .from('profiles')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) {
        console.error('[Auth] update profile failed', error);
        throw error;
      }
      setProfile((prev) => (prev ? ({ ...prev, ...patch } as Profile) : prev));
    },
    [user]
  );

  const setFamilyName = useCallback(
    async (name: string) => {
      const clean = name.trim();
      await updateProfile({ family_name: clean || null });
    },
    [updateProfile]
  );

  // Promote the current user to 'owner' if they currently are a 'member'.
  // Called by FamilyContext the first time the user creates a person record
  // (i.e. they are establishing the family tree, so they own it).
  const promoteToOwnerIfNeeded = useCallback(async () => {
    if (!user) return;
    if (!profile) return;
    if (profile.role === 'owner' || profile.role === 'admin') return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: 'owner', updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) {
        console.error('[Auth] promote to owner failed', error);
        return;
      }
      setProfile((prev) => (prev ? { ...prev, role: 'owner' } : prev));
    } catch (err) {
      console.error('[Auth] promote to owner exception', err);
    }
  }, [user, profile]);

  // ─────────────────────────────────────────────────────────────────────────
  // Phase 9 — Paid Membership Recognition Sync.
  //
  // Resolves the case where a user successfully paid (e.g. yearly Founder's
  // $14.99) but their local `profiles.plan` is still `free` because the
  // activate-subscription handshake didn't complete on the original tab
  // (network blip, tab closed early, paid before profile was fully bound,
  // etc.). Calls the additive `sync-membership-from-payments` edge
  // function which:
  //   • Looks up the Stripe customer by the user's auth email
  //     (case-insensitive, trimmed)
  //   • Finds the highest active/trialing/past_due subscription
  //   • Maps a known KinsCircle price (or recurring interval) to a Plan
  //   • Updates the matching profiles row's plan + stripe ids ONLY when
  //     the gateway plan is HIGHER than the local plan (no downgrades)
  //
  // After a successful upgrade we re-fetch the local profile so the UI
  // (Header, Pricing, MembershipBanner, Account, etc.) flips to the
  // correct paid state immediately. NEVER throws — caller-safe.
  // ─────────────────────────────────────────────────────────────────────────
  const syncMembershipFromPayments = useCallback<
    AuthContextType['syncMembershipFromPayments']
  >(async () => {
    try {
      const targetEmail = user?.email ?? profile?.email ?? null;
      if (!user || !targetEmail) return null;
      const { data, error } = await supabase.functions.invoke(
        'sync-membership-from-payments',
        { body: { email: targetEmail, userId: user.id } },
      );
      if (error) {
        console.warn('[Auth] sync-membership-from-payments invoke error:', error.message);
        return null;
      }
      const resp = (data ?? {}) as any;
      if (resp.ok === false) {
        console.warn('[Auth] sync-membership-from-payments returned error:', resp.error);
        return null;
      }
      const updated = resp.updatedProfile === true;
      const newPlan: Plan | null = (resp.plan as Plan) ?? null;
      const reason: string = String(resp.reason ?? '');
      console.log('[Auth] payment-sync', {
        email: targetEmail,
        matched: resp.matched,
        plan: newPlan,
        status: resp.status,
        updated,
        reason,
      });
      if (updated) {
        // Re-read the profile so UI flips to the paid state immediately.
        await fetchProfile(user.id, user.email);
      }
      return { updated, plan: newPlan, reason };
    } catch (err: any) {
      console.warn('[Auth] sync-membership-from-payments exception:', err?.message || err);
      return null;
    }
  }, [user, profile?.email, fetchProfile]);

  // Auto-sync once per session when:
  //   • a real authenticated user exists,
  //   • the local profile is loaded (so we don't race the initial fetch), AND
  //   • the local plan is currently `free`
  //
  // This catches the exact "paid but app still shows Upgrade" case
  // without ever interfering with users who are already correctly
  // recognized as paid (no API call fires for them). Runs at most once
  // per `user.id` per page load.
  const autoSyncFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !profile) return;
    if (profile.plan && profile.plan !== 'free') return;
    if (autoSyncFiredRef.current === user.id) return;
    autoSyncFiredRef.current = user.id;
    // Slight delay so the first paint isn't blocked by a network call.
    const t = window.setTimeout(() => {
      syncMembershipFromPayments().catch((e) =>
        console.warn('[Auth] auto payment-sync threw:', e),
      );
    }, 600);
    return () => window.clearTimeout(t);
  }, [user, profile, syncMembershipFromPayments]);


  // ─────────────────────────────────────────────────────────────────────────
  // DEV BYPASS — DISABLED.
  // The temporary auto-login-as-owner shortcut has been turned off so the app
  // now requires a real Supabase session for any authenticated functionality.
  // (Re-enable for local debugging only by flipping DEV_BYPASS_AUTH to true.)
  // ─────────────────────────────────────────────────────────────────────────
  const DEV_BYPASS_AUTH = false;

  // ─────────────────────────────────────────────────────────────────────────
  // DEV_BYPASS_TEST_MODE — billing/Stripe/trial/paywall bypass + dev owner role.
  // Active ONLY on deploypad.app dev/staging hosts (see src/lib/devBypass.ts).
  //
  // Does NOT auto-authenticate — the user must still have a real Supabase
  // session. When active AND the user is authenticated, we synthesize:
  //   1. an "annual_founding" plan so isPaid / isMember / paywall gates open
  //      up (the modern relationship engine becomes reachable end-to-end
  //      without going through Stripe);
  //   2. an "owner" role so `isStaff` / `isOwner` flip to true, which lets
  //      AddGraphMemberModal auto-approve new members (no "Submit for
  //      approval" workflow) and surfaces the staff-only debug + repair
  //      tools while testing the dev project. This matches the spec:
  //      "in DEV_BYPASS_TEST_MODE on deploypad.app: authenticated user
  //      should be treated as owner/admin for testing only".
  //
  // The Supabase `profiles` row is NEVER mutated — both the plan and role
  // overrides live entirely in the React context. Production hosts are
  // completely unaffected (DEV_BYPASS_TEST_MODE === false everywhere
  // except *.deploypad.app).
  // ─────────────────────────────────────────────────────────────────────────
  const realPlan: Plan = profile?.plan ?? 'free';
  const realRole: Role = profile?.role ?? 'member';
  const realIsAuthenticated = !!user;

  // Plan precedence: dev-bypass-auth > test-mode-bypass > real plan.
  const planFromBypassAuth: Plan | null =
    DEV_BYPASS_AUTH && !realIsAuthenticated ? 'annual_founding' : null;
  const planFromTestMode: Plan | null =
    DEV_BYPASS_TEST_MODE && realIsAuthenticated && realPlan === 'free'
      ? 'annual_founding'
      : null;

  // Role precedence: dev-bypass-auth > test-mode-bypass > real role.
  // The test-mode role override only kicks in when the real role is
  // 'member' — anyone whose DB row already says 'owner' or 'admin' keeps
  // their real role unchanged so nothing about their identity shifts.
  const roleFromTestMode: Role | null =
    DEV_BYPASS_TEST_MODE && realIsAuthenticated && realRole === 'member'
      ? 'owner'
      : null;

  const plan: Plan = planFromBypassAuth ?? planFromTestMode ?? realPlan;
  const role: Role =
    DEV_BYPASS_AUTH && !realIsAuthenticated
      ? 'owner'
      : roleFromTestMode ?? realRole;
  const isAuthenticated = DEV_BYPASS_AUTH ? true : realIsAuthenticated;

  // When bypass is active and there is no real profile, synthesize one so
  // downstream consumers (Header badges, SmtpSettings page, etc.) work.
  // With bypass disabled this branch is never taken — `profile` is used as-is.
  const effectiveProfile: Profile | null =
    DEV_BYPASS_AUTH && !profile
      ? {
          id: user?.id ?? 'dev-bypass-owner',
          email: user?.email ?? 'mrjmoore49@gmail.com',
          name: 'Owner (dev bypass)',
          family_name: null,
          plan: 'annual_founding',
          role: 'owner',
          stripe_customer_id: null,
          stripe_subscription_id: null,
        }
      : (planFromTestMode || roleFromTestMode) && profile
      ? // Test-mode bypass: keep the real profile but virtually upgrade
        // plan + role so any consumer that reads profile.plan / profile.role
        // directly (a few legacy spots — most read isPaid / isStaff from
        // context) sees the bypassed values. Database row is NEVER touched.
        {
            ...profile,
            plan: planFromTestMode ?? profile.plan,
            role: roleFromTestMode ?? profile.role,
          }
      : profile;

  // isPaid / isFree / isOwner / isAdmin reflect the *effective* identity so
  // the rest of the app behaves as if a real owner is signed in.
  const isPaid = isAuthenticated && plan !== 'free';
  const isFree = isAuthenticated && plan === 'free';
  const isOwner = isAuthenticated && role === 'owner';
  const isAdmin = isAuthenticated && role === 'admin';
  const isStaff = isOwner || isAdmin;

  // One-time bypass banner — only fires on deploypad.app hosts.
  const bypassBannerLogged = useRef(false);
  useEffect(() => {
    if (!bypassBannerLogged.current) {
      logDevBypassBanner();
      bypassBannerLogged.current = true;
    }
  }, []);

  // Debug: log plan + role whenever they change so it's easy to verify
  // that the logged-in user is recognized as paid / owner / admin.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (DEV_BYPASS_AUTH && !realIsAuthenticated) {
      console.warn('[Auth] ⚠️ DEV BYPASS active — auto-logged-in as owner. Disable in src/contexts/AuthContext.tsx before going live.');
    }
    if (planFromTestMode) {
      console.warn(
        '[Auth] ⚠️ DEV_BYPASS_TEST_MODE active on',
        typeof window !== 'undefined' ? window.location.hostname : '?',
        '— plan virtually upgraded to annual_founding (no DB write, no Stripe).',
      );
    }
    if (roleFromTestMode) {
      console.warn(
        '[Auth] ⚠️ DEV_BYPASS_TEST_MODE active on',
        typeof window !== 'undefined' ? window.location.hostname : '?',
        '— role virtually upgraded to owner for testing (no DB write).',
        '{ authUid:', user?.id, ', realRole:', realRole, '}',
      );
    }
    console.log('USER PLAN:', plan);
    console.log('USER ROLE:', role);
    console.log('[Auth] isPaid =', isPaid, '| isAdmin =', isAdmin, '| isOwner =', isOwner);
  }, [isAuthenticated, realIsAuthenticated, plan, role, isPaid, isAdmin, isOwner, planFromTestMode, roleFromTestMode, user?.id, realRole]);





  const value: AuthContextType = {
    user,
    session,
    loading,
    profileLoading,
    profileError,
    profile: effectiveProfile,
    plan,
    role,
    isPaid,
    isFree,
    isAuthenticated,
    isOwner,
    isAdmin,
    isStaff,
    memberEmail: user?.email ?? effectiveProfile?.email ?? null,
    memberName: effectiveProfile?.name ?? null,
    familyName: effectiveProfile?.family_name ?? null,
    signUp,
    signIn,
    signOut,
    sendPasswordReset,
    resendConfirmationEmail,
    sendMagicLink,

    updatePassword,
    refreshProfile,
    updateProfile,
    setFamilyName,
    promoteToOwnerIfNeeded,
    syncMembershipFromPayments,
  };




  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
